import React, { useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { useConfigId } from '../../contexts/ConfigContext'
import {
  activateLlmFallback,
  clearLlmFallback,
  getGraphSettings,
  getLLMModels,
  getLlmFallbackStatus,
  updateSetting,
} from '../../api/graph_settings'
import { EXECUTOR_SETTING_KEYS } from '../../config/graphEntities'
import { PageSection, Select, SettingsFormWithPanel, Switch } from '../../components/ui'
import '../../styles/pages/page-common.css'
import '../../styles/pages/settings.css'

const BOOLEAN_SETTING_KEYS = new Set([
  'bot_typing_indicator_enabled',
  'bot_send_split_enabled',
  'bot_send_strict_paid_invariants',
  'bot_policy_safety_enabled',
  'llm_fallback_to_grok_enabled',
  'llm_state_model_router_enabled',
  'timewaster_strategy_enabled',
])

const LLM_SETTING_KEYS = [
  'llm_fallback_to_grok_enabled',
  'llm_fallback_grok_model',
  'llm_state_model_router_enabled',
  'llm_state_model_router',
]

const MESSAGE_GENERATOR_STATES = [
  'RAPPORT',
  'FLIRT',
  'PRESELL',
  'OFFER',
  'POSTBUY',
  'RETENTION',
  'REENGAGE',
  'default',
]

const ROUTER_AGENTS = [
  { key: 'message_generator', states: MESSAGE_GENERATOR_STATES },
  { key: 'offer_generator', states: ['*'] },
  { key: 'proactive_message_generator', states: ['*'] },
]

function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

function normalizeLocalSettings(settings = {}) {
  const next = { ...settings }
  for (const key of BOOLEAN_SETTING_KEYS) {
    if (key in settings) next[key] = readBool(settings[key])
  }
  next.llm_fallback_to_grok_enabled = readBool(settings.llm_fallback_to_grok_enabled ?? true)
  next.llm_state_model_router_enabled = readBool(settings.llm_state_model_router_enabled ?? false)
  next.llm_fallback_grok_model = settings.llm_fallback_grok_model || ''
  next.llm_state_model_router = normalizeRouter(settings.llm_state_model_router)
  return next
}

function sameSettingValue(key, a, b) {
  if (BOOLEAN_SETTING_KEYS.has(key)) return readBool(a) === readBool(b)
  if (key === 'llm_state_model_router') {
    return JSON.stringify(normalizeRouter(a)) === JSON.stringify(normalizeRouter(b))
  }
  return String(a ?? '') === String(b ?? '')
}

function normalizeRouter(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return {}
}

function modelOption(model) {
  const id = typeof model === 'string' ? model : model?.id
  if (!id) return null
  const provider = typeof model === 'object' ? model.provider : (
    id.startsWith('minimax:') ? 'minimax' :
    id.startsWith('grok') ? 'grok' :
    id.startsWith('groq:') ? 'groq' :
    id.startsWith('openrouter:') ? 'openrouter' :
    'openai'
  )
  const rawLabel = typeof model === 'object' ? (model.label || id) : id
  const label = rawLabel === id ? id : `${rawLabel} (${id})`
  return { value: id, label, provider }
}

function uniqModelOptions(models, extraValues = []) {
  const byId = new Map()
  for (const item of models || []) {
    const opt = modelOption(item)
    if (opt && !byId.has(opt.value)) byId.set(opt.value, opt)
  }
  for (const value of extraValues) {
    const id = String(value || '').trim()
    if (id && !byId.has(id)) byId.set(id, modelOption(id))
  }
  return Array.from(byId.values()).filter(Boolean)
}

function getRouterModel(router, agentKey, state) {
  return router?.[agentKey]?.[state] || ''
}

function setRouterModel(router, agentKey, state, model) {
  const next = normalizeRouter(router)
  const agentRouter = { ...(next[agentKey] || {}) }
  if (model) {
    agentRouter[state] = model
  } else {
    delete agentRouter[state]
  }
  return { ...next, [agentKey]: agentRouter }
}

export default function SettingsExecutorPage() {
  const { t } = useI18n()
  const configId = useConfigId()
  const [data, setData] = useState(null)
  const [local, setLocal] = useState({})
  const [loading, setLoading] = useState(true)
  const [llmModels, setLlmModels] = useState([])
  const [fallbackStatus, setFallbackStatus] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!configId) return
    Promise.all([
      getGraphSettings(configId),
      getLlmFallbackStatus(configId).catch(() => null),
    ])
      .then(([settingsData, status]) => {
        setData(settingsData)
        setFallbackStatus(status)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [configId])

  useEffect(() => {
    getLLMModels()
      .then(({ models }) => setLlmModels(Array.isArray(models) ? models : []))
      .catch(() => setLlmModels([]))
  }, [])

  useEffect(() => {
    if (data?.settings) setLocal(normalizeLocalSettings(data.settings))
  }, [data?.settings])

  const settings = data?.settings || {}
  const normalizedSettings = normalizeLocalSettings(settings)
  const allSettingKeys = [...EXECUTOR_SETTING_KEYS, ...LLM_SETTING_KEYS]
  const dirty = allSettingKeys.some(
    (k) => !sameSettingValue(k, local[k], normalizedSettings[k])
  )
  const router = normalizeRouter(local.llm_state_model_router)
  const selectedModelValues = [
    local.llm_fallback_grok_model,
    ...ROUTER_AGENTS.flatMap((agent) => agent.states.map((state) => getRouterModel(router, agent.key, state))),
  ]
  const modelOptions = uniqModelOptions(llmModels, selectedModelValues)

  async function onSave() {
    setSaving(true)
    setError(null)
    try {
      for (const k of allSettingKeys) {
        const v = local[k]
        if (v !== undefined && !sameSettingValue(k, v, normalizedSettings[k])) {
          await updateSetting(k, v, configId)
        }
      }
      setData((prev) => ({ ...prev, settings: { ...prev?.settings, ...local } }))
      setFallbackStatus(await getLlmFallbackStatus(configId).catch(() => fallbackStatus))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error && !data) return <p className="settings-error">{error}</p>

  async function handleActivateFallback() {
    setSaving(true)
    setError(null)
    try {
      const result = await activateLlmFallback({ reason: 'manual', source_model: 'minimax:MiniMax-M2.5-highspeed' }, configId)
      setFallbackStatus(result?.status || await getLlmFallbackStatus(configId))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleClearFallback() {
    setSaving(true)
    setError(null)
    try {
      await clearLlmFallback(configId)
      setFallbackStatus(await getLlmFallbackStatus(configId))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsFormWithPanel dirty={dirty} saving={saving} onSave={onSave}>
      <PageSection
        title={t('settings.executor.title')}
        contentClassName="settings-section-block"
      >
        {error && <p className="settings-error">{error}</p>}
        <p className="settings-node-desc">{t('settings.executor.intro')}</p>
        <div className="settings-llm-panel">
          <div className="settings-llm-header">
            <div>
              <h3>{t('settings.llmFallback.title')}</h3>
              <p className="settings-node-desc">{t('settings.llmFallback.desc')}</p>
            </div>
            <span className={`settings-status-pill ${fallbackStatus?.active ? 'settings-status-pill--active' : ''}`}>
              {fallbackStatus?.active ? t('settings.llmFallback.active') : t('settings.llmFallback.inactive')}
            </span>
          </div>
          <div className="settings-grid">
            <div className="setting-row">
              <div className="setting-label-wrap">
                <label className="setting-key">{t('settings.key.llm_fallback_to_grok_enabled')}</label>
                <span className="setting-desc">{t('settings.desc.llm_fallback_to_grok_enabled')}</span>
              </div>
              <Switch
                checked={readBool(local.llm_fallback_to_grok_enabled)}
                onChange={(checked) => setLocal((s) => ({ ...s, llm_fallback_to_grok_enabled: checked }))}
                ariaLabel={t('settings.key.llm_fallback_to_grok_enabled')}
                className="setting-switch"
              />
            </div>
            <div className="setting-row">
              <div className="setting-label-wrap">
                <label className="setting-key">{t('settings.key.llm_fallback_grok_model')}</label>
                <span className="setting-desc">{t('settings.desc.llm_fallback_grok_model')}</span>
              </div>
              <Select
                options={modelOptions}
                value={local.llm_fallback_grok_model}
                onChange={(value) => setLocal((s) => ({ ...s, llm_fallback_grok_model: value }))}
                placeholder={t('settings.llm.selectModel')}
                dropdownPosition="down"
                dropdownAutoWidth
                className="setting-select"
              />
            </div>
          </div>
          <div className="settings-llm-actions">
            <button type="button" className="setting-save" onClick={handleActivateFallback} disabled={saving}>
              {t('settings.llmFallback.activate')}
            </button>
            <button type="button" className="settings-secondary-button" onClick={handleClearFallback} disabled={saving}>
              {t('settings.llmFallback.clear')}
            </button>
            <span className="setting-desc">
              {t('settings.llmFallback.current')}: {fallbackStatus?.fallback_model || local.llm_fallback_grok_model || 'n/a'}
            </span>
          </div>
        </div>

        <div className="settings-llm-panel">
          <div className="settings-llm-header">
            <div>
              <h3>{t('settings.llmRouter.title')}</h3>
              <p className="settings-node-desc">{t('settings.llmRouter.desc')}</p>
            </div>
            <Switch
              checked={readBool(local.llm_state_model_router_enabled)}
              onChange={(checked) => setLocal((s) => ({ ...s, llm_state_model_router_enabled: checked }))}
              ariaLabel={t('settings.key.llm_state_model_router_enabled')}
              className="setting-switch"
            />
          </div>
          <div className="settings-router-grid">
            {ROUTER_AGENTS.map((agent) => (
              <div key={agent.key} className="settings-router-card">
                <h4>{agent.key}</h4>
                {agent.states.map((state) => (
                  <div key={`${agent.key}:${state}`} className="settings-router-row">
                    <span className="settings-router-state">{state}</span>
                    <Select
                      options={modelOptions}
                      value={getRouterModel(router, agent.key, state)}
                      onChange={(value) => setLocal((s) => ({
                        ...s,
                        llm_state_model_router: setRouterModel(s.llm_state_model_router, agent.key, state, value),
                      }))}
                      placeholder={t('settings.llm.inheritModel')}
                      dropdownPosition="down"
                      dropdownAutoWidth
                      className="setting-select"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="settings-grid">
          {EXECUTOR_SETTING_KEYS.map((keyName) => (
            <div key={keyName} className="setting-row">
              <div className="setting-label-wrap">
                <label className="setting-key">{t(`settings.key.${keyName}`) || keyName}</label>
                {t(`settings.desc.${keyName}`) && t(`settings.desc.${keyName}`) !== `settings.desc.${keyName}` && (
                  <span className="setting-desc">{t(`settings.desc.${keyName}`)}</span>
                )}
              </div>
              {BOOLEAN_SETTING_KEYS.has(keyName) ? (
                <Switch
                  checked={readBool(local[keyName])}
                  onChange={(checked) => setLocal((s) => ({ ...s, [keyName]: checked }))}
                  ariaLabel={t(`settings.key.${keyName}`) || keyName}
                  className="setting-switch"
                />
              ) : (
                <input
                  type="text"
                  value={String(local[keyName] ?? '')}
                  onChange={(e) => setLocal((s) => ({ ...s, [keyName]: e.target.value }))}
                  className="setting-value"
                />
              )}
            </div>
          ))}
        </div>
      </PageSection>
    </SettingsFormWithPanel>
  )
}
